import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import socket from "../socket";


export default function WaiterTableView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [search, setSearch] = useState('');
  const [quantities, setQuantities] = useState({});

  const [table, setTable] = useState(null);

  useEffect(() => {
    api.get(`/tables/${id}`).then(res => setTable(res.data));
  }, [id]);

  useEffect(() => {
  function onOrderUpdated(data) {
    if (data.tableId === id) {
      api.get(`/tables/${id}/orders`).then((res) => {
        setOrders(res.data || []);
      });
    }
  }

  function onTableUpdated(data) {
    if (data.tableId === id) {
      api.get(`/tables/${id}`).then((res) => setTable(res.data));
      api.get(`/tables/${id}/orders`).then((res) => setOrders(res.data));
    }
  }

  socket.on("orderUpdated", onOrderUpdated);
  socket.on("tableUpdated", onTableUpdated);

  return () => {
    socket.off("orderUpdated", onOrderUpdated);
    socket.off("tableUpdated", onTableUpdated);
  };
}, [id]);



  // Load orders
  useEffect(() => {
    async function loadOrders() {
      setLoading(true);
      try {
        const res = await api.get(`/tables/${id}/orders`);
        setOrders(res.data || []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    loadOrders();
  }, [id]);

  // Load menu
  useEffect(() => {
    async function loadMenu() {
      try {
        const res = await api.get('/menu');
        setMenu(res.data || []);
      } catch (e) {
        console.error(e);
      }
    }
    loadMenu();
  }, []);

  function openConfirmModal(action) {
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  }

  async function deleteOrder(orderId) {
    try {
      await api.delete(`/orders/${orderId}`);
      const updated = orders.filter(o => o._id !== orderId);
      setOrders(updated);

      if (updated.length === 0) {
        await api.put(`/tables/${id}/free`);
      }

      const res = await api.get(`/tables/${id}/orders`);
      setOrders(res.data || []);

      socket.emit("orderUpdated", {
        tableId: id,
        action: "delete",
      });

    } catch (e) {
      console.error(e);
      alert('Delete failed');
    }
  }

  async function freeTable() {
    try {
      setOrders([]);
      setShowConfirmModal(false);

      await api.delete(`/tables/${id}/orders/all`);
      await api.put(`/tables/${id}/free`);

      navigate('/waiter', { state: { reload: true } });

      socket.emit("tableUpdated", {
        tableId: id,
        status: "free",
        orderCount: 0,
      });

    } catch (e) {
      console.error(e);
      alert('Free failed');
    }
  }

  async function addItemToTable(itemId) {
    try {
      const qty = quantities[itemId] || 1;
      const menuItem = menu.find(m => m._id === itemId);
      if (!menuItem) return;

      const newOrder = {
        _id: `temp-${Date.now()}`,
        items: [{ item: menuItem, qty }],
        total: menuItem.price * qty,
        createdAt: new Date()
      };
      setOrders(prev => [...prev, newOrder]);

      setShowAddModal(false);
      setSearch('');
      setQuantities({});

      await api.post(`/tables/${id}/orders`, { items: [{ item: itemId, qty }] });

      socket.emit("orderUpdated", {
        tableId: id,
        action: "add",
      });

    } catch (e) {
      console.error(e);
      alert('Failed to add order');
    }
  }

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseInt(value) || 1;
    setQuantities(prev => ({ ...prev, [itemId]: numValue > 0 ? numValue : 1 }));
  };

  const incrementQuantity = (itemId) => {
    setQuantities(prev => ({ ...prev, [itemId]: (prev[itemId] || 1) + 1 }));
  };

  const decrementQuantity = (itemId) => {
    setQuantities(prev => ({ ...prev, [itemId]: Math.max(1, (prev[itemId] || 1) - 1) }));
  };

  const filteredMenu = menu.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{table?.name}</h2>
        <div>
          <button className="bg-gray-300 px-3 py-1 rounded mr-2" onClick={() => navigate('/waiter')}>
            Back
          </button>
          <button className="bg-green-600 text-white px-3 py-1 rounded mr-2" onClick={() => setShowAddModal(true)}>
            Add Order
          </button>
          <button
            className="bg-yellow-500 text-white px-3 py-1 rounded"
            onClick={() => openConfirmModal(freeTable)}
          >
            Free Table
          </button>
        </div>
      </div>

      <div className="bg-white p-3 rounded shadow">
        <h3 className="font-semibold mb-2">Current Orders</h3>
        {loading ? (
          <div>Loading...</div>
        ) : orders.length === 0 ? (
          <div className="text-gray-500">No orders for this table.</div>
        ) : (
          <div className="grid gap-2">
            {orders.map((o) => (
              <div key={o._id} className="flex justify-between items-center p-2 border rounded">
                <div>
                  <div className="font-medium">
                    {o.items?.map(it => it.item ? `${it.item.name} × ${it.qty}` : `Item × ${it.qty}`).join(', ')}
                  </div>
                  <div className="text-sm text-gray-600">Total: ₹ {o.total || 0}</div>
                  <div className="text-xs text-gray-500">Created: {new Date(o.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <button
                    className="bg-red-500 text-white px-2 py-1 rounded"
                    onClick={() => openConfirmModal(() => deleteOrder(o._id))}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Order Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg w-[500px] shadow-lg">
            <h3 className="text-lg font-semibold mb-3">Add Order</h3>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items..."
              className="border p-2 w-full mb-3 rounded"
            />

            <div className="max-h-96 overflow-y-auto mb-3 border rounded">
              {filteredMenu.length > 0 ? (
                filteredMenu.map(m => {
                  const currentQty = quantities[m._id] || 0;
                  return (
                    <div key={m._id} className="flex justify-between items-center px-3 py-2 border-b hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="font-medium">{m.name}</div>
                        <div className="text-sm text-gray-600">₹{m.price}</div>
                      </div>

                      <div className="flex items-center gap-2 mr-3">
                        <button
                          onClick={() => handleQuantityChange(m._id, currentQty - 1)}
                          className="bg-gray-200 hover:bg-gray-300 w-8 h-8 rounded flex items-center justify-center font-bold"
                        >−</button>

                        <input
                          type="number"
                          min="0"
                          value={currentQty}
                          onChange={(e) => handleQuantityChange(m._id, parseInt(e.target.value) || 0)}
                          className="w-16 text-center border rounded py-1"
                        />

                        <button
                          onClick={() => handleQuantityChange(m._id, currentQty + 1)}
                          className="bg-gray-200 hover:bg-gray-300 w-8 h-8 rounded flex items-center justify-center font-bold"
                        >+</button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 text-gray-500 text-center">No items found</div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
                onClick={() => {
                  setShowAddModal(false);
                  setQuantities({});
                  setSearch('');
                }}
              >
                Close
              </button>

              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                onClick={async () => {
                  const itemsToAdd = Object.entries(quantities)
                    .filter(([_, qty]) => qty > 0)
                    .map(([itemId, qty]) => {
                      const menuItem = menu.find(m => m._id === itemId);
                      return menuItem ? { item: menuItem, qty } : null;
                    })
                    .filter(Boolean);

                  if (itemsToAdd.length === 0) return alert('Select at least one item');

                  setShowAddModal(false);
                  setSearch('');
                  setQuantities({});

                  const newOrders = itemsToAdd.map(it => ({
                    _id: `temp-${Date.now()}-${it.item._id}`,
                    items: [{ item: it.item, qty: it.qty }],
                    total: it.item.price * it.qty,
                    createdAt: new Date()
                  }));
                  setOrders(prev => [...prev, ...newOrders]);

                  try {
                    for (const it of itemsToAdd) {
                      await api.post(`/tables/${id}/orders`, {
                        items: [{ item: it.item._id, qty: it.qty }]
                      });
                    }
                  } catch (e) {
                    console.error(e);
                    alert('Failed to add order');
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 w-80 shadow-lg text-center">
            <h3 className="text-lg font-semibold mb-3">Are you sure?</h3>
            <div className="flex justify-center gap-4">
              <button
                className="bg-red-500 text-white px-4 py-1 rounded"
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmAction && confirmAction();
                }}
              >
                Yes
              </button>
              <button
                className="bg-gray-300 px-4 py-1 rounded"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
